import React, { useState, useEffect, useRef, useMemo, createContext, useContext, Component } from 'react';
import {
    StyleSheet, Text, View, TouchableOpacity,
    ScrollView, Dimensions, Animated, Modal, TextInput, useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useIsFocused } from '@react-navigation/native';
import Data from './Data';
import { getStorage, setStorage } from '../storage';
import { useDarkMode } from './common';

// ════════════════════════════════════════════════════════
// 에러 바운더리 — 렌더링 크래시 시 자동 복구
// ════════════════════════════════════════════════════════
const maskName = (n: string) => { if (!n || n.length <= 1) return n; if (n.length === 2) return n[0] + "*"; return n[0] + "*".repeat(n.length - 2) + n[n.length - 1]; };
class ErrorBoundary extends Component<{ children: React.ReactNode }, { hasError: boolean }> {
    state = { hasError: false };
    static getDerivedStateFromError() { return { hasError: true }; }
    componentDidCatch() {
        // 3초 후 자동 새로고침
        setTimeout(() => { window.location.reload(); }, 3000);
    }
    render() {
        if (this.state.hasError) {
            return (
                <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#0F172A' }}>
                    <Ionicons name="reload-outline" size={40} color="#94A3B8" />
                    <Text style={{ color: '#E2E8F0', fontSize: 16, fontWeight: '600', marginTop: 16 }}>
                        화면을 다시 불러오는 중...
                    </Text>
                    <Text style={{ color: '#94A3B8', fontSize: 13, marginTop: 8 }}>
                        3초 후 자동 새로고침됩니다
                    </Text>
                </View>
            );
        }
        return this.props.children;
    }
}

// ════════════════════════════════════════════════════════
// 테마
// ════════════════════════════════════════════════════════
const ThemeContext = createContext<{ dark: boolean; toggle: () => void }>({
    dark: true, toggle: () => {},
});
const useTheme = () => useContext(ThemeContext);

const DARK = {
    bg:        '#0F172A',
    card:      '#1E293B',
    cardInner: '#0F172A',
    border:    'rgba(99,179,237,0.12)',
    text:      '#E2E8F0',
    textSub:   '#94A3B8',
    muted:     '#a8adb497',
    trackBg:   'rgba(255,255,255,0.05)',
};
const LIGHT = {
    bg:        '#F9FAFB',
    card:      '#FFFFFF',
    cardInner: '#F3F4F6',
    border:    '#E5E7EB',
    text:      '#111827',
    textSub:   '#4B5563',
    muted:     '#9CA3AF',
    trackBg:   '#F3F4F6',
};
type Theme = typeof DARK;

const S = {
    accent:  '#38BDF8',
    ok:      '#34D399',
    err:     '#F87171',
    diaper:  '#EF4444',   // 빨간색
    reset:   '#FB923C',
    replace: '#FBBF24',
    noMove:  '#64748B',
    move:    '#3B82F6',    // 움직임 파란색
    water:   '#FB923C',    // 물방울 주황
    waterLo: '#FDBA74',    // 물방울 연한 주황
};

// ★ 화면 폭은 모듈 상수로 잡으면 안 된다. Dimensions.get 은 로드 시점에 한 번만 읽혀
// 회전해도 갱신되지 않는다 — 가로로 열고 세로로 돌리면 290px 알림 패널이 그대로 남아
// 격자에 100px 남짓만 남는 사고가 났다. 폭은 컴포넌트 안에서 useWindowDimensions 로 본다.
const BLINK_DURATION_MS = 5000;

// ════════════════════════════════════════════════════════
// 타입
// ════════════════════════════════════════════════════════
type SensorStatus = 'ok' | 'offline' | 'diaper' | 'noMove' | 'reset' | 'replace' | 'init';
type GridMode = 4 | 8 | 16;

interface SensorSlot {
    sensor_id: string;
    room_no:   string | null;
    name:      string | null;
    status:    SensorStatus;
    value:     number | null;
    last_value: number | null;
    hasDiaperAlarm: boolean;
    regdate:   string | null;
    battery:   number | null;
    connect:   number | null;
    alarmOff:  boolean;         // ALARM===0 → 소변알림 끔
    posture:   string | null;   // POSTURE (standup/laydown/left/right)
    raw:       any;             // 원본 센서 데이터 (SensorDetail 이동용)
}

interface RepeaterItem {
    id:      string;
    label:   string;
    status:  'ok' | 'offline';
    sensors: SensorSlot[];
}

interface AlarmItem {
    AREA:       string;
    SENSOR_ID:  string;
    NAME?:      string;
    ROOM_NO:    string;
    AMOUNT:     number;
    LAST_VALUE: number;
    VALUE:      number | null;
    ALARM_TYPE: number;
    REGDATE:    string;
}

// ════════════════════════════════════════════════════════
// 상수 · 헬퍼
// ════════════════════════════════════════════════════════
const STATUS_META: Record<SensorStatus, { label: string; color: string }> = {
    ok:      { label: '정상',         color: S.ok      },
    offline: { label: '연결 끊김',    color: S.err     },
    diaper:  { label: '기저귀 교체',  color: S.diaper  },
    noMove:  { label: '움직임 없음',  color: S.noMove  },
    reset:   { label: '센서 초기화',  color: S.reset   },
    replace: { label: '센서 교체',    color: S.replace },
    init:    { label: '초기화 알림',  color: S.ok      },
};

const ALARM_TYPE_META: Record<number, { label: string; color: string }> = {
    [0]:  { label: '기저귀 교체', color: S.diaper  },
    [-3]: { label: '초기화 알림', color: S.ok      },
    [-4]: { label: '센서 초기화', color: S.reset   },
    [-5]: { label: '센서 교체',   color: S.replace },
    [-6]: { label: '움직임 없음', color: S.noMove  },
    [-7]: { label: '움직임 감지', color: S.move    },
};
const getAlarmTypeMeta = (type: number) =>
    ALARM_TYPE_META[type] ?? { label: '알림', color: S.noMove };

const alarmKey = (a: AlarmItem) => `${a.SENSOR_ID}_${a.ALARM_TYPE}_${a.REGDATE}`;

const getTimeAgo = (regdate: string) => {
    if (!regdate || regdate === 'move') return '방금 전';
    const diffMin = Math.floor((Date.now() - new Date(regdate).getTime()) / 60000);
    if (isNaN(diffMin)) return '';
    if (diffMin < 1)  return '방금 전';
    if (diffMin < 60) return `${diffMin}분 전`;
    const h = Math.floor(diffMin / 60), m = diffMin % 60;
    if (h < 24) return `${h}시간 ${m}분 전`;
    return `${Math.floor(h / 24)}일 전`;
};

// 배터리: 70+ 초록, 40~69 노랑, 39이하 빨강
const batteryColor = (b: number | null): string => {
    if (b == null) return S.noMove;
    if (b >= 70) return S.ok;
    if (b >= 40) return S.replace;
    return S.err;
};
const batteryIcon = (b: number | null): string => {
    if (b == null) return 'battery-dead-outline';
    if (b >= 70) return 'battery-full-outline';
    if (b >= 40) return 'battery-half-outline';
    return 'battery-dead-outline';
};

const getSensorStatus = (sensor: any, sensorAlarms: AlarmItem[]): SensorStatus => {
    if (sensor.CONNECT !== 0) return 'offline';
    if (sensorAlarms.length > 0) {
        const types = sensorAlarms.map((a: AlarmItem) => a.ALARM_TYPE);
        if (types.includes(0))  return 'diaper';
        if (types.includes(-3)) return 'init';
        if (types.includes(-5)) return 'replace';
        if (types.includes(-4)) return 'reset';
    }
    // BEDSORE_ALARM 테이블: POSTURE_ALARM === 1 → 움직임 없음 (기존 -6 대체)
    if (sensor.POSTURE_ALARM === 1) return 'noMove';
    return 'ok';
};

const buildRepeaters = (
    repeaterList: any[], sensorMap: Record<string, any[]>, alarmList: AlarmItem[],
): RepeaterItem[] => {
    return repeaterList.map(rp => {
        const rpId = rp.ID;
        const label = rp.ROOM_NO || rpId;
        const rpSensors = sensorMap[rpId] || [];
        const slots: SensorSlot[] = rpSensors.map(sensor => {
            const sAlarms = sensor.CONNECT === 0
                ? alarmList.filter(a => a.SENSOR_ID === sensor.SENSOR_ID) : [];
            const status = getSensorStatus(sensor, sAlarms);
            const diaperAlarm = sAlarms.find(a => a.ALARM_TYPE === 0);
            return {
                sensor_id: sensor.SENSOR_ID,
                room_no:   sensor.ROOM_NO,
                name:      sensor.NAME,
                status,
                value:   diaperAlarm ? diaperAlarm.VALUE : null,
                last_value: sensor.LAST_VALUE ?? sensor.VALUE ?? null,
                hasDiaperAlarm: !!diaperAlarm,
                regdate: sAlarms.length > 0 ? getTimeAgo(sAlarms[0].REGDATE) : null,
                battery: sensor.BATTERY ?? null,
                connect: sensor.CONNECT ?? null,
                alarmOff: sensor.ALARM === 0,
                posture:  sensor.DETECTION_ALARM === 1
                    && isInDetectionTime(sensor.DETECTION_ST, sensor.DETECTION_END)
                    ? (sensor.POSTURE || null) : null,
                raw: sensor,
            };
        });
        const rpStatus: 'ok' | 'offline' = rp.CONNECT !== 0 ? 'offline' : 'ok';
        return { id: rpId, label, status: rpStatus, sensors: slots };
    });
};

const getRepeaterTopColor = (r: RepeaterItem): string =>
    r.status === 'offline' ? S.err : S.ok;

// DETECTION_ST ~ DETECTION_END 시간 범위 체크 (datetime → 시/분만 비교)
const isInDetectionTime = (st: any, end: any): boolean => {
    if (!st || !end) return true; // 시간 미설정 → 항상 활성
    const stDate = new Date(st);
    const endDate = new Date(end);
    if (isNaN(stDate.getTime()) || isNaN(endDate.getTime())) return true;
    const stMin  = stDate.getHours() * 60 + stDate.getMinutes();
    const endMin = endDate.getHours() * 60 + endDate.getMinutes();
    const now    = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    if (endMin < stMin) { // 자정 넘어가는 경우 (예: 23:00 ~ 06:00)
        return nowMin >= stMin || nowMin <= endMin;
    }
    if (endMin === stMin) return nowMin === stMin;
    return nowMin >= stMin && nowMin <= endMin;
};

const getPostureInfo = (posture: string | null): { icon: string; label: string; color: string; rotation: string } | null => {
    if (!posture || posture === '') return null;
    const p = posture.toLowerCase();
    if (p.includes('standup')) return { icon: 'walk-outline',   label: '움직임', color: S.move,   rotation: '0deg' };
    return null;
};

// ════════════════════════════════════════════════════════
// 공통 컴포넌트
// ════════════════════════════════════════════════════════

const Dot = ({ color, size = 10 }: { color: string; size?: number }) => {
    const { dark } = useTheme();
    const T = dark ? DARK : LIGHT;
    return (
        <View style={{
            width: size, height: size, borderRadius: size / 2,
            backgroundColor: color,
            shadowColor: color, shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.8, shadowRadius: size * 0.5,
            elevation: 3, borderWidth: 1.5, borderColor: T.card,
        }} />
    );
};

// ─── 물방울 (AMOUNT 기반 채워짐, 주황색) ────────────
// 물방울: LAST_VALUE 기반, 60→40%, 알람→90%
const WaterDrop = ({ lastValue, hasAlarm, alarmOff, size = 14 }: {
    lastValue: number | null; hasAlarm: boolean; alarmOff: boolean; size?: number;
}) => {
    // 소변알림 꺼짐 → 빗금 표시
    if (alarmOff) {
        return (
            <View style={{ width: size, height: size, position: 'relative' }}>
                <Ionicons name="water" size={size} color={S.noMove + '30'} />
                <View style={{
                    position: 'absolute',
                    width: size, height: 1.5,
                    backgroundColor: S.err,
                    top: size / 2 - 0.75, left: 0,
                    transform: [{ rotate: '-45deg' }],
                }} />
            </View>
        );
    }
    // 값 없음 → 회색 물방울 외곽선(명확히 보이게)
    if (lastValue == null || lastValue <= 0) {
        return <Ionicons name="water-outline" size={size} color={'#94A3B8'} />;
    }
    // 60→40%, 알람→90% (넘치지 않게)
    const ratio = hasAlarm ? 0.9 : Math.min(0.85, Math.max(0.05, (lastValue / 150)));
    const fillH = Math.max(1, Math.round(size * ratio));
    const color = hasAlarm ? S.err : ratio >= 0.4 ? S.water : S.waterLo;

    return (
        <View style={{ width: size, height: size, position: 'relative', overflow: 'hidden' }}>
            <Ionicons name="water" size={size} color={color + '55'}
                style={{ position: 'absolute', top: 0, left: 0 }} />
            <View style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                height: fillH, overflow: 'hidden',
            }}>
                <Ionicons name="water" size={size} color={color}
                    style={{ position: 'absolute', bottom: 0, left: 0 }} />
            </View>
        </View>
    );
};

// ─── 자세 표시 (애니메이션) ──────────────────────────
const PostureDisplay = ({ posture, size = 26 }: { posture: string; size?: number }) => {
    const pi = getPostureInfo(posture);
    if (!pi) return null;
    const isMoving = posture.toLowerCase().includes('standup');
    const bounce = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        if (!isMoving) { bounce.setValue(0); return; }
        const loop = Animated.loop(Animated.sequence([
            Animated.timing(bounce, { toValue: 3, duration: 300, useNativeDriver: true }),
            Animated.timing(bounce, { toValue: 0,  duration: 300, useNativeDriver: true }),
        ]));
        loop.start();
        return () => loop.stop();
    }, [isMoving]);

    return (
        <View style={{ alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            <Animated.View style={isMoving ? { transform: [{ translateY: bounce }] } : undefined}>
                <Ionicons name={pi.icon as any} size={size} color={pi.color}
                    style={{ transform: [{ rotate: pi.rotation }] }} />
            </Animated.View>
            {size >= 36 && <Text style={{ fontSize: 9, fontWeight: '700', color: pi.color }}>{pi.label}</Text>}
        </View>
    );
};

// ─── 알림 카드 (우측 패널용) ─────────────────────────
const AlarmCardPanel = ({ alarm, isNew }: { alarm: AlarmItem; isNew: boolean }) => {
    const { dark } = useTheme();
    const T    = dark ? DARK : LIGHT;
    const meta = getAlarmTypeMeta(alarm.ALARM_TYPE);
    const blink = useRef(new Animated.Value(1)).current;

    useEffect(() => {
        if (!isNew) { blink.setValue(1); return; }
        const loop = Animated.loop(Animated.sequence([
            Animated.timing(blink, { toValue: 0.25, duration: 400, useNativeDriver: true }),
            Animated.timing(blink, { toValue: 1,    duration: 400, useNativeDriver: true }),
        ]));
        loop.start();
        const t = setTimeout(() => { loop.stop(); blink.setValue(1); }, BLINK_DURATION_MS);
        return () => { loop.stop(); clearTimeout(t); };
    }, [isNew]);

    return (
        <Animated.View style={[
            alarmSt.item,
            { backgroundColor: T.card, borderLeftColor: meta.color },
            isNew && { opacity: blink },
        ]}>
            {isNew && (
                <View style={[alarmSt.newBadge, { backgroundColor: meta.color }]}>
                    <Text style={alarmSt.newBadgeText}>NEW</Text>
                </View>
            )}
            <View style={[alarmSt.dotWrap, { backgroundColor: meta.color + '18' }]}>
                <Dot color={meta.color} size={12} />
            </View>
            <View style={{ flex: 1, gap: 2 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <Text style={[alarmSt.repeaterLabel, { color: T.text }]}>{alarm.ROOM_NO}</Text>
                    {alarm.NAME ? <Text style={[alarmSt.sub, { color: T.muted }]}>{maskName(alarm.NAME)}</Text> : null}
                </View>
                <Text style={[alarmSt.msg, { color: meta.color }]}>{meta.label}</Text>
            </View>
            <View style={{ gap: 3, alignItems: 'flex-end' }}>
                {alarm.ALARM_TYPE === 0 && alarm.VALUE != null && (
                    <View style={[alarmSt.badge, { borderColor: meta.color + '40' }]}>
                        <Text style={[alarmSt.badgeText, { color: meta.color }]}>{alarm.VALUE}</Text>
                    </View>
                )}
                <Text style={[alarmSt.time, { color: T.muted }]}>{getTimeAgo(alarm.REGDATE)}</Text>
            </View>
        </Animated.View>
    );
};

// ─── 우측 알람 피드 ───────────────────────────────────
const AlarmFeed = ({ alarms, newKeys }: { alarms: AlarmItem[]; newKeys: Set<string> }) => {
    const { dark } = useTheme();
    const T = dark ? DARK : LIGHT;
    const scrollY = useRef(new Animated.Value(0)).current;
    const [cH, setCH] = useState(0);
    const [vH, setVH] = useState(0);
    const hasAlarm = alarms.length > 0;

    const sorted = useMemo(() =>
        [...alarms].sort((a, b) => new Date(b.REGDATE).getTime() - new Date(a.REGDATE).getTime()),
        [alarms]
    );

    const trackBg = dark ? 'rgba(148,163,184,0.08)' : 'rgba(15,23,42,0.07)';
    const thumbBg = dark ? 'rgba(148,163,184,0.38)' : 'rgba(71,85,105,0.28)';
    const thumbH  = vH > 0 && cH > 0 ? Math.max(24, (vH / cH) * vH) : 24;
    const thumbTop = scrollY.interpolate({
        inputRange:  [0, Math.max(cH - vH, 1)],
        outputRange: [0, vH - thumbH],
        extrapolate: 'clamp',
    });

    return (
        <View style={[apSt.panel, { backgroundColor: T.bg }]}>
            <View style={[apSt.header, { borderBottomColor: T.border }]}>
                <View style={apSt.headerLeft}>
                    <Dot color={hasAlarm ? S.diaper : S.ok} size={8} />
                    <Text style={[apSt.title, { color: T.textSub }]}>
                        실시간 알림{hasAlarm ? ` (${alarms.length})` : ''}
                    </Text>
                </View>
                {hasAlarm && (
                    <View style={apSt.liveBadge}>
                        <Dot color={S.err} size={5} />
                        <Text style={apSt.liveText}>LIVE</Text>
                    </View>
                )}
            </View>

            {!hasAlarm ? (
                <View style={apSt.empty}>
                    <View style={[apSt.emptyDot, { backgroundColor: S.ok + '20' }]}>
                        <Dot color={S.ok} size={16} />
                    </View>
                    <Text style={[apSt.emptyText, { color: T.muted }]}>현재 이상 없음</Text>
                </View>
            ) : (
                <View style={{ flex: 1 }}>
                    <ScrollView
                        showsVerticalScrollIndicator={false}
                        scrollEventThrottle={16}
                        onScroll={Animated.event(
                            [{ nativeEvent: { contentOffset: { y: scrollY } } }],
                            { useNativeDriver: false }
                        )}
                        onContentSizeChange={(_, h) => setCH(h)}
                        onLayout={e => setVH(e.nativeEvent.layout.height)}
                        contentContainerStyle={apSt.feedList}
                    >
                        {sorted.map((a, i) => (
                            <AlarmCardPanel
                                key={alarmKey(a) + i}
                                alarm={a}
                                isNew={newKeys.has(alarmKey(a))}
                            />
                        ))}
                    </ScrollView>
                    {cH > vH && (
                        <View style={[apSt.scrollTrack, { backgroundColor: trackBg }]}>
                            <Animated.View style={[apSt.scrollThumb, { height: thumbH, backgroundColor: thumbBg, top: thumbTop }]} />
                        </View>
                    )}
                </View>
            )}

        </View>
    );
};

// ════════════════════════════════════════════════════════
// 센서 등록 모달
// ════════════════════════════════════════════════════════
const STATUS_OPTIONS = [
    { value: 0, label: '해당없음' },
    { value: 1, label: '욕창' },
    { value: 2, label: '욕창위험' },
    { value: 3, label: '낙상' },
    { value: 4, label: '요로감염' },
];
const CLASS_OPTIONS = [
    { value: '0', label: '일자형' },
    { value: '1', label: '삼각체결' },
];

const INITIAL_FORM = {
    NAME: '', GENDER: '', BIRTH: '', ADDRESS: '',
    GUARDIAN_NAME: '', GUARDIAN_PHONE: '', ROOM_NO: '', BED_NO: '',
    SENSOR_ID: '', PHONE: '', CLASSIFICATION: '0', DIAPER_CODE: '',
    STATUS: [] as number[],
};

// 라디오 버튼
const RadioGroup = ({ options, value, onChange, T }: {
    options: { value: any; label: string }[]; value: any;
    onChange: (v: any) => void; T: Theme;
}) => (
    <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
        {options.map(o => {
            const active = o.value === value;
            return (
                <TouchableOpacity key={String(o.value)} onPress={() => onChange(o.value)}
                    style={[modalSt.radioBtn, {
                        backgroundColor: active ? S.accent + '18' : T.cardInner,
                        borderColor: active ? S.accent : T.border,
                    }]}>
                    <View style={[modalSt.radioDot, {
                        borderColor: active ? S.accent : T.muted,
                        backgroundColor: active ? S.accent : 'transparent',
                    }]} />
                    <Text style={{ color: active ? S.accent : T.text, fontSize: 13, fontWeight: active ? '700' : '500' }}>{o.label}</Text>
                </TouchableOpacity>
            );
        })}
    </View>
);

// 체크박스 (다중선택)
const CheckGroup = ({ options, values, onChange, T }: {
    options: { value: any; label: string }[]; values: any[];
    onChange: (v: any[]) => void; T: Theme;
}) => (
    <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
        {options.map(o => {
            const active = values.includes(o.value);
            return (
                <TouchableOpacity key={String(o.value)} onPress={() => {
                    if (active) {
                        onChange(values.filter(v => v !== o.value));
                    } else if (o.value === 0) {
                        onChange([0]);
                    } else {
                        onChange([...values.filter(v => v !== 0), o.value]);
                    }
                }}
                    style={[modalSt.radioBtn, {
                        backgroundColor: active ? S.accent + '18' : T.cardInner,
                        borderColor: active ? S.accent : T.border,
                    }]}>
                    <View style={[modalSt.radioDot, {
                        borderColor: active ? S.accent : T.muted,
                        backgroundColor: active ? S.accent : 'transparent',
                        borderRadius: 3,
                    }]} />
                    <Text style={{ color: active ? S.accent : T.text, fontSize: 13, fontWeight: active ? '700' : '500' }}>{o.label}</Text>
                </TouchableOpacity>
            );
        })}
    </View>
);

const AddSensorModal = ({ visible, onClose, repeaterId, area, roomNo, T }: {
    visible: boolean; onClose: (success?: boolean) => void;
    repeaterId: string; area: string; roomNo: string; T: Theme;
}) => {
    const [form, setForm] = useState({ ...INITIAL_FORM, ROOM_NO: roomNo });
    const [submitting, setSubmitting] = useState(false);
    const [personList, setPersonList] = useState<any[]>([]);
    const [diaperList, setDiaperList] = useState<any[]>([]);
    const [personOpen, setPersonOpen] = useState(false);
    const [diaperOpen, setDiaperOpen] = useState(false);
    const [personSearch, setPersonSearch] = useState('');

    // 모달 열릴 때 데이터 로드
    useEffect(() => {
        if (!visible) return;
        setForm({ ...INITIAL_FORM, ROOM_NO: roomNo });
        setPersonOpen(false);
        setDiaperOpen(false);
        setPersonSearch('');
        // 대상자 목록
        Data.fetchData('tabSensor/getPersonList', {}, (result: any, count: any) => {
            setPersonList(count > 0 ? result : []);
        }, true);
        // 기저귀 목록
        Data.fetchData('tabSensor/getDiaperList', {}, (result: any, count: any) => {
            setDiaperList(count > 0 ? result.filter((d: any) => d.USE_YN === 0) : []);
        }, true);
    }, [visible, roomNo]);

    const set = (key: string, val: any) => setForm(prev => ({ ...prev, [key]: val }));

    // ISO datetime → YYYY-MM-DD
    const toDateStr = (v: any) => {
        if (!v) return '';
        const d = new Date(v);
        if (isNaN(d.getTime())) return String(v).substring(0, 10);
        return d.toISOString().substring(0, 10);
    };

    const selectPerson = (person: any) => {
        setForm(prev => ({
            ...prev,
            NAME: person.NAME || '',
            BIRTH: toDateStr(person.BIRTH),
            GENDER: person.GENDER || '',
            ADDRESS: person.ADDRESS || '',
            PHONE: person.PHONE || '',
            GUARDIAN_NAME: person.GUARDIAN_NAME || '',
            GUARDIAN_PHONE: person.GUARDIAN_PHONE || '',
        }));
        setPersonOpen(false);
        setPersonSearch('');
    };

    const filteredPersons = personSearch
        ? personList.filter(p => (p.NAME || '').includes(personSearch))
        : personList;

    const handleSubmit = () => {
        if (!form.NAME.trim()) { alert('대상자를 선택해주세요.'); return; }
        if (!form.SENSOR_ID.trim()) { alert('센서 ID를 입력해주세요.'); return; }
        if (form.STATUS.length === 0) { alert('특이사항을 선택해주세요.'); return; }
        if (!form.DIAPER_CODE) { alert('기저귀 종류를 선택해주세요.'); return; }
        setSubmitting(true);
        Data.fetchData('tabSensor/insertSensor', {
            ...form,
            REPEATER_ID: repeaterId,
            AREA: area,
            STATUS_LIST: form.STATUS.length > 0
                ? form.STATUS.map(s => ({ STATUS: s, BIRTH: form.BIRTH }))
                : [{ STATUS: 0, BIRTH: form.BIRTH }],
        }, (result: any, count: any) => {
            setSubmitting(false);
            if (count === -1 && result?.message) {
                alert(result.message);
            } else {
                alert('센서가 등록되었습니다.');
                onClose(true);
            }
        }, true);
    };

    const selectedDiaper = diaperList.find(d => d.DIAPER_CODE === form.DIAPER_CODE);

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
            <View style={modalSt.overlay}>
                <View style={[modalSt.container, { backgroundColor: T.card, borderColor: T.border }]}>
                    {/* 헤더 */}
                    <View style={[modalSt.header, { borderBottomColor: T.border }]}>
                        <View>
                            <Text style={[modalSt.title, { color: T.text }]}>센서 등록</Text>
                            <Text style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
                                중계기: {roomNo} · {repeaterId}
                            </Text>
                        </View>
                        <TouchableOpacity style={modalSt.closeBtn} onPress={onClose}>
                            <Ionicons name="close" size={22} color={T.muted} />
                        </TouchableOpacity>
                    </View>

                    {/* 본문 */}
                    <ScrollView
                        style={modalSt.body}
                        contentContainerStyle={{ gap: 14, paddingBottom: 24 }}
                        showsVerticalScrollIndicator={false}
                    >
                        {/* ── 대상자 선택 ── */}
                        <View style={[modalSt.section, { borderColor: T.border }]}>
                            <Text style={[modalSt.sectionTitle, { color: T.text }]}>
                                <Ionicons name="person-outline" size={14} color={S.accent} /> 대상자 선택 *
                            </Text>
                            <TouchableOpacity
                                style={[modalSt.dropdown, { backgroundColor: T.cardInner, borderColor: T.border }]}
                                onPress={() => { setPersonOpen(!personOpen); setDiaperOpen(false); }}
                            >
                                <Text style={{ color: form.NAME ? T.text : T.muted, fontSize: 14 }}>
                                    {form.NAME ? `${maskName(form.NAME)} (${form.GENDER || '-'}, ${form.BIRTH || '-'})` : '대상자를 선택하세요'}
                                </Text>
                                <Ionicons name={personOpen ? 'chevron-up' : 'chevron-down'} size={16} color={T.muted} />
                            </TouchableOpacity>
                            {personOpen && (
                                <View style={[modalSt.dropdownList, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                                    <TextInput
                                        style={[modalSt.searchInput, { backgroundColor: T.bg, borderColor: T.border, color: T.text }]}
                                        placeholder="이름 검색..."
                                        placeholderTextColor={T.muted}
                                        value={personSearch}
                                        onChangeText={setPersonSearch}
                                    />
                                    <ScrollView style={{ maxHeight: 150 }} nestedScrollEnabled>
                                        {filteredPersons.length === 0 ? (
                                            <Text style={{ color: T.muted, fontSize: 13, padding: 10, textAlign: 'center' }}>결과 없음</Text>
                                        ) : filteredPersons.map((p, i) => (
                                            <TouchableOpacity key={i} onPress={() => selectPerson(p)}
                                                style={[modalSt.dropdownItem, { borderBottomColor: T.border }]}>
                                                <Text style={{ color: T.text, fontSize: 13, fontWeight: '600' }}>{maskName(p.NAME)}</Text>
                                                <Text style={{ color: T.muted, fontSize: 11 }}>{p.GENDER || '-'} · {toDateStr(p.BIRTH) || '-'}</Text>
                                            </TouchableOpacity>
                                        ))}
                                    </ScrollView>
                                </View>
                            )}
                        </View>

                        {/* ── 센서 정보 ── */}
                        <View style={[modalSt.section, { borderColor: T.border }]}>
                            <Text style={[modalSt.sectionTitle, { color: T.text }]}>
                                <Ionicons name="hardware-chip-outline" size={14} color={S.accent} /> 센서 정보
                            </Text>
                            <View>
                                <Text style={[modalSt.label, { color: T.textSub }]}>센서 ID *</Text>
                                <TextInput style={[modalSt.input, { backgroundColor: T.cardInner, borderColor: T.border, color: T.text }]}
                                    value={form.SENSOR_ID} onChangeText={v => set('SENSOR_ID', v)}
                                    placeholder="센서 ID 입력" placeholderTextColor={T.muted} />
                            </View>
                            <View style={{ flexDirection: 'row', gap: 10 }}>
                                <View style={{ flex: 1 }}>
                                    <Text style={[modalSt.label, { color: T.textSub }]}>호실</Text>
                                    <TextInput style={[modalSt.input, { backgroundColor: T.cardInner, borderColor: T.border, color: T.text }]}
                                        value={form.ROOM_NO} onChangeText={v => set('ROOM_NO', v)}
                                        placeholder="호실" placeholderTextColor={T.muted} />
                                </View>
                                <View style={{ flex: 1 }}>
                                    <Text style={[modalSt.label, { color: T.textSub }]}>침대번호</Text>
                                    <TextInput style={[modalSt.input, { backgroundColor: T.cardInner, borderColor: T.border, color: T.text }]}
                                        value={form.BED_NO} onChangeText={v => set('BED_NO', v)}
                                        placeholder="침대번호" placeholderTextColor={T.muted} />
                                </View>
                            </View>
                        </View>

                        {/* ── 특이사항 ── */}
                        <View style={[modalSt.section, { borderColor: T.border }]}>
                            <Text style={[modalSt.sectionTitle, { color: T.text }]}>
                                <Ionicons name="alert-circle-outline" size={14} color={S.replace} /> 특이사항 *
                            </Text>
                            <CheckGroup options={STATUS_OPTIONS} values={form.STATUS} onChange={v => set('STATUS', v)} T={T} />
                        </View>

                        {/* ── 기저귀 설정 ── */}
                        <View style={[modalSt.section, { borderColor: T.border }]}>
                            <Text style={[modalSt.sectionTitle, { color: T.text }]}>
                                <Ionicons name="options-outline" size={14} color={S.accent} /> 기저귀 설정
                            </Text>
                            {/* 착용 방법 */}
                            <View>
                                <Text style={[modalSt.label, { color: T.textSub }]}>착용 방법</Text>
                                <RadioGroup options={CLASS_OPTIONS} value={form.CLASSIFICATION} onChange={v => set('CLASSIFICATION', v)} T={T} />
                            </View>
                            {/* 기저귀 종류 */}
                            <View>
                                <Text style={[modalSt.label, { color: T.textSub }]}>기저귀 종류 *</Text>
                                <TouchableOpacity
                                    style={[modalSt.dropdown, { backgroundColor: T.cardInner, borderColor: T.border }]}
                                    onPress={() => { setDiaperOpen(!diaperOpen); setPersonOpen(false); }}
                                >
                                    <Text style={{ color: selectedDiaper ? T.text : T.muted, fontSize: 14 }}>
                                        {selectedDiaper ? selectedDiaper.DIAPER_NAME : '선택하세요'}
                                    </Text>
                                    <Ionicons name={diaperOpen ? 'chevron-up' : 'chevron-down'} size={16} color={T.muted} />
                                </TouchableOpacity>
                                {diaperOpen && (
                                    <View style={[modalSt.dropdownList, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                                        <ScrollView style={{ maxHeight: 150 }} nestedScrollEnabled>
                                            {diaperList.map((d, i) => (
                                                <TouchableOpacity key={i}
                                                    onPress={() => { set('DIAPER_CODE', d.DIAPER_CODE); setDiaperOpen(false); }}
                                                    style={[modalSt.dropdownItem, {
                                                        borderBottomColor: T.border,
                                                        backgroundColor: form.DIAPER_CODE === d.DIAPER_CODE ? S.accent + '12' : 'transparent',
                                                    }]}>
                                                    <Text style={{ color: form.DIAPER_CODE === d.DIAPER_CODE ? S.accent : T.text, fontSize: 13, fontWeight: '600' }}>
                                                        {d.DIAPER_NAME}
                                                    </Text>
                                                </TouchableOpacity>
                                            ))}
                                        </ScrollView>
                                    </View>
                                )}
                            </View>
                        </View>

                        {/* ── 보호자 정보 ── */}
                        <View style={[modalSt.section, { borderColor: T.border }]}>
                            <Text style={[modalSt.sectionTitle, { color: T.text }]}>
                                <Ionicons name="people-outline" size={14} color={S.accent} /> 보호자 정보
                            </Text>
                            <View style={{ flexDirection: 'row', gap: 10 }}>
                                <View style={{ flex: 1 }}>
                                    <Text style={[modalSt.label, { color: T.textSub }]}>보호자 이름</Text>
                                    <TextInput style={[modalSt.input, { backgroundColor: T.cardInner, borderColor: T.border, color: T.text }]}
                                        value={form.GUARDIAN_NAME} onChangeText={v => set('GUARDIAN_NAME', v)}
                                        placeholder="이름" placeholderTextColor={T.muted} />
                                </View>
                                <View style={{ flex: 1 }}>
                                    <Text style={[modalSt.label, { color: T.textSub }]}>보호자 연락처</Text>
                                    <TextInput style={[modalSt.input, { backgroundColor: T.cardInner, borderColor: T.border, color: T.text }]}
                                        value={form.GUARDIAN_PHONE} onChangeText={v => set('GUARDIAN_PHONE', v)}
                                        placeholder="연락처" placeholderTextColor={T.muted} />
                                </View>
                            </View>
                        </View>
                    </ScrollView>

                    {/* 버튼 */}
                    <View style={[modalSt.footer, { borderTopColor: T.border }]}>
                        <TouchableOpacity
                            style={[modalSt.btn, { backgroundColor: T.cardInner, borderColor: T.border }]}
                            onPress={onClose}
                        >
                            <Text style={{ color: T.text, fontWeight: '600', fontSize: 14 }}>취소</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                            style={[modalSt.btn, { backgroundColor: S.accent, borderColor: S.accent, opacity: submitting ? 0.5 : 1 }]}
                            onPress={handleSubmit}
                            disabled={submitting}
                        >
                            <Text style={{ color: '#fff', fontWeight: '700', fontSize: 14 }}>
                                {submitting ? '등록 중...' : '등록'}
                            </Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const AddRepeaterModal = ({ visible, onClose, area, T }: {
    visible: boolean; onClose: (success?: boolean) => void;
    area: string; T: Theme;
}) => {
    const [form, setForm] = useState({ ROOM_NO: '', ID: '', IPV4: '' });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!visible) return;
        setForm({ ROOM_NO: '', ID: '', IPV4: '' });
    }, [visible]);

    const set = (key: string, val: any) => setForm(prev => ({ ...prev, [key]: val }));

    const handleSubmit = () => {
        if (!form.ROOM_NO.trim()) { alert('병실 호수를 입력해주세요.'); return; }
        if (!form.ID.trim()) { alert('중계기 ID를 입력해주세요.'); return; }
        if (!form.IPV4.trim()) { alert('IPv4 주소를 입력해주세요.'); return; }
        setSubmitting(true);
        Data.fetchData('tabRepeater/insertNewRepeater', {
            ROOM_NO: form.ROOM_NO,
            ID: form.ID,
            IPV4: form.IPV4,
            AREA: area,
        }, (result: any, count: any) => {
            setSubmitting(false);
            if (count === 1) {
                alert('중계기가 등록되었습니다.');
                onClose(true);
            } else if (count === -2) {
                alert('이미 등록된 병실 호수입니다.');
            } else if (count === -3) {
                alert('이미 등록된 중계기 ID입니다.');
            } else {
                alert('등록에 실패하였습니다.');
            }
        }, true);
    };

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
            <View style={modalSt.overlay}>
                <View style={[modalSt.container, { backgroundColor: T.card, borderColor: T.border, width: 400 }]}>
                    {/* 헤더 */}
                    <View style={[modalSt.header, { borderBottomColor: T.border }]}>
                        <View>
                            <Text style={[modalSt.title, { color: T.text }]}>중계기 등록</Text>
                            <Text style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
                                구역: {area}
                            </Text>
                        </View>
                        <TouchableOpacity style={modalSt.closeBtn} onPress={onClose}>
                            <Ionicons name="close" size={22} color={T.muted} />
                        </TouchableOpacity>
                    </View>

                    {/* 본문 */}
                    <View style={[modalSt.body, { paddingBottom: 0 }]}>
                        <View style={[modalSt.section, { borderColor: T.border }]}>
                            <Text style={[modalSt.sectionTitle, { color: T.text }]}>
                                <Ionicons name="radio-outline" size={14} color={S.accent} /> 중계기 정보
                            </Text>
                            <View>
                                <Text style={[modalSt.label, { color: T.textSub }]}>병실 호수 *</Text>
                                <TextInput style={[modalSt.input, { backgroundColor: T.cardInner, borderColor: T.border, color: T.text }]}
                                    value={form.ROOM_NO} onChangeText={v => set('ROOM_NO', v)}
                                    placeholder="예: 101" placeholderTextColor={T.muted} />
                            </View>
                            <View>
                                <Text style={[modalSt.label, { color: T.textSub }]}>중계기 ID *</Text>
                                <TextInput style={[modalSt.input, { backgroundColor: T.cardInner, borderColor: T.border, color: T.text }]}
                                    value={form.ID} onChangeText={v => set('ID', v)}
                                    placeholder="중계기 ID 입력" placeholderTextColor={T.muted} />
                            </View>
                            <View>
                                <Text style={[modalSt.label, { color: T.textSub }]}>IPv4 주소 *</Text>
                                <TextInput style={[modalSt.input, { backgroundColor: T.cardInner, borderColor: T.border, color: T.text }]}
                                    value={form.IPV4} onChangeText={v => set('IPV4', v)}
                                    placeholder="예: 192.168.0.1" placeholderTextColor={T.muted} />
                            </View>
                        </View>
                    </View>

                    {/* 버튼 */}
                    <View style={[modalSt.footer, { borderTopColor: T.border }]}>
                        <TouchableOpacity
                            style={[modalSt.btn, { backgroundColor: T.cardInner, borderColor: T.border }]}
                            onPress={onClose}
                        >
                            <Text style={{ color: T.text, fontWeight: '600', fontSize: 14 }}>취소</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                            style={[modalSt.btn, { backgroundColor: S.accent, borderColor: S.accent, opacity: submitting ? 0.5 : 1 }]}
                            onPress={handleSubmit}
                            disabled={submitting}
                        >
                            <Text style={{ color: '#fff', fontWeight: '700', fontSize: 14 }}>
                                {submitting ? '등록 중...' : '등록'}
                            </Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

// ════════════════════════════════════════════════════════
// 센서 카드 (ROOM_NO 제거, 이름만 표시)
// ════════════════════════════════════════════════════════
const SensorSlotCard = ({ sensor, compact, gridMode, sensorCount, T, onPress, onAnalysis }: {
    sensor: SensorSlot; compact: boolean; gridMode: GridMode; sensorCount: number; T: Theme; onPress?: () => void; onAnalysis?: () => void;
}) => {
    const meta       = STATUS_META[sensor.status] || { label: '', color: '#94A3B8' };
    const color      = meta.color;
    const batColor   = batteryColor(sensor.battery);
    const batIcon    = batteryIcon(sensor.battery);
    const isConnected = sensor.connect === 0;

    const isStandup = sensor.posture?.toLowerCase().includes('standup');
    const cardColor = isStandup ? S.move : color;

    // 분할별 자세 아이콘 크기: 4분할=44, 8분할=32(6센서=28), 16분할=18
    const postureSize = gridMode === 4 ? 44
        : gridMode === 8 ? (sensorCount > 4 ? 28 : 32)
        : 18;

    if (compact) {
        return (
            <TouchableOpacity activeOpacity={0.7} onPress={onPress} style={[
                slotSt.compactCard,
                { backgroundColor: T.cardInner, borderColor: cardColor + '40', borderLeftColor: cardColor }
            ]}>
                <View style={slotSt.compactIcons}>
                    <Ionicons name="wifi" size={10} color={isConnected ? S.accent : S.err} />
                    <WaterDrop lastValue={sensor.last_value} hasAlarm={sensor.hasDiaperAlarm} alarmOff={sensor.alarmOff} size={11} />
                    <Ionicons name={batIcon as any} size={11} color={batColor} />
                    {onAnalysis ? (
                        <TouchableOpacity onPress={onAnalysis} hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}>
                            <Ionicons name="bar-chart" size={12} color={S.ok} />
                        </TouchableOpacity>
                    ) : null}
                </View>
                <Text style={[slotSt.compactName, { color: T.text }]} numberOfLines={1}>{maskName(sensor.name) || '-'}</Text>
                <Text style={[slotSt.compactStatus, { color }]} numberOfLines={1}>{meta.label}</Text>
                {sensor.posture ? (
                    <View style={{ position: 'absolute', right: 3, top: 0, bottom: 0, justifyContent: 'center' }}>
                        <PostureDisplay posture={sensor.posture} size={postureSize} />
                    </View>
                ) : null}
            </TouchableOpacity>
        );
    }

    return (
        <TouchableOpacity activeOpacity={0.7} onPress={onPress} style={[
            slotSt.card,
            { backgroundColor: T.cardInner, borderColor: cardColor + '44', borderLeftColor: cardColor }
        ]}>
            <View style={slotSt.cardTop}>
                <Dot color={color} size={8} />
                <View style={slotSt.iconRow}>
                    <Ionicons name="wifi" size={14} color={isConnected ? S.accent : S.err} />
                    <WaterDrop lastValue={sensor.last_value} hasAlarm={sensor.hasDiaperAlarm} alarmOff={sensor.alarmOff} size={15} />
                    <View style={[slotSt.batBadge, { backgroundColor: batColor + '18', borderColor: batColor + '35' }]}>
                        <Ionicons name={batIcon as any} size={13} color={batColor} />
                        {sensor.battery != null && (
                            <Text style={[slotSt.batText, { color: batColor }]}>{sensor.battery}%</Text>
                        )}
                    </View>
                </View>
            </View>
            <Text style={[slotSt.sensorName, { color: T.text }]} numberOfLines={1}>{maskName(sensor.name) || '-'}</Text>
            <View style={slotSt.statusRow}>
                <Text style={[slotSt.statusLabel, { color }]} numberOfLines={1}>{meta.label}</Text>
                {sensor.value != null && (
                    <View style={[slotSt.valueBadge, { borderColor: color + '40' }]}>
                        <Text style={[slotSt.valueText, { color }]}>{sensor.value}%</Text>
                    </View>
                )}
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4 }}>
                {sensor.regdate ? <Text style={[slotSt.lastSeen, { color: T.muted }]}>{sensor.regdate}</Text> : null}
                {onAnalysis ? (
                    <TouchableOpacity onPress={onAnalysis}
                        style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4, backgroundColor: S.ok + '20', borderWidth: 1, borderColor: S.ok + '55', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 8 }}>
                        <Ionicons name="bar-chart" size={14} color={S.ok} />
                        <Text style={{ fontSize: 12, fontWeight: '700', color: S.ok }}>분석</Text>
                    </TouchableOpacity>
                ) : null}
            </View>
            {/* 자세 표시 - 절대위치, 8분할+6센서일 때만 살짝 아래 */}
            {sensor.posture ? (
                <View style={{
                    position: 'absolute', right: 6, top: 0,
                    bottom: (gridMode === 8 && sensorCount > 4) ? -8 : 0,
                    justifyContent: 'center',
                }}>
                    <PostureDisplay posture={sensor.posture} size={postureSize} />
                </View>
            ) : null}
        </TouchableOpacity>
    );
};

// ════════════════════════════════════════════════════════
// 중계기 카드 (flex:1로 화면 채움)
// ════════════════════════════════════════════════════════
const RepeaterCard = ({ repeater, gridMode, T, onAdd, onSensorPress, onSensorAnalysis, isViewer }: {
    repeater: RepeaterItem; gridMode: GridMode; T: Theme; onAdd: () => void; onSensorPress: (raw: any) => void; onSensorAnalysis?: (raw: any) => void; isViewer?: boolean;
}) => {
    const isOffline   = repeater.status === 'offline';
    const topColor    = getRepeaterTopColor(repeater);
    const sensorCount = repeater.sensors.length;
    // ★ 칸이 작아지는 조건은 분할 모드만으로 정해지지 않는다.
    // 아래 sensorCols/sensorRowCount 가 말하듯 센서가 5개 이상이면 열·행이 3개로 늘어
    // 칸이 급격히 작아지는데, 그때도 풀 카드를 그려서 내용이 칸 밖으로 넘쳐 잘렸다.
    // (화면 크기와 무관 — 비율이 같아 큰 화면에서도 똑같이 잘린다)
    const compact     = gridMode === 16 || sensorCount > 4;

    // 4분할/16분할: 5~6개→3열2행, 8분할: 5~6개→2열3행, 나머지 2열2행 고정
    const sensorCols = (gridMode === 4 || gridMode === 16) && sensorCount > 4 ? 3 : 2;
    const sensorRowCount = gridMode === 8 && sensorCount > 4 ? 3 : 2;

    return (
        <View style={[rpSt.card, { flex: 1, backgroundColor: T.card, borderColor: T.border, borderTopColor: topColor }]}>
            {/* 헤더 */}
            <View style={rpSt.header}>
                <View style={rpSt.headerLeft}>
                    <View style={[rpSt.iconBox, {
                        backgroundColor: isOffline ? S.err + '15' : S.accent + '12',
                        borderColor:     isOffline ? S.err + '30' : S.accent + '22',
                    }]}>
                        <Ionicons name="radio-outline" size={compact ? 12 : 16} color={isOffline ? S.err : S.accent} />
                    </View>
                    <Text style={[rpSt.rpLabel, { color: T.text, fontSize: compact ? 12 : 15 }]} numberOfLines={1}>
                        {repeater.label}
                    </Text>
                    {!isViewer && sensorCount < 6 && (
                        <TouchableOpacity
                            style={[rpSt.addBtn, { borderColor: T.border }]}
                            activeOpacity={0.6}
                            onPress={onAdd}
                        >
                            <Ionicons name="add" size={compact ? 11 : 14} color={S.accent} />
                        </TouchableOpacity>
                    )}
                </View>
                {isOffline ? (
                    <View style={[rpSt.chip, { backgroundColor: S.err + '18' }]}>
                        <Dot color={S.err} size={5} />
                        <Text style={[rpSt.chipText, { color: S.err }]}>끊김</Text>
                    </View>
                ) : (
                    <View style={[rpSt.chip, { backgroundColor: S.ok + '15' }]}>
                        <Dot color={S.ok} size={5} />
                        <Text style={[rpSt.chipText, { color: S.ok }]}>정상</Text>
                    </View>
                )}
            </View>

            {/* 센서 그리드 - flex:1로 남은 공간 채움 */}
            <View style={[rpSt.sensorGrid, { flex: 1 }]}>
                {sensorCount === 0 ? (
                    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                        <Text style={{ color: T.muted, fontSize: compact ? 11 : 13 }}>등록된 센서 없음</Text>
                    </View>
                ) : (
                    Array.from({ length: sensorRowCount }, (_, row) => (
                        <View key={row} style={{ flex: 1, flexDirection: 'row', gap: compact ? 3 : 5 }}>
                            {Array.from({ length: sensorCols }, (_, col) => {
                                const idx = row * sensorCols + col;
                                if (idx >= sensorCount) return <View key={col} style={{ flex: 1 }} />;
                                return (
                                    <View key={col} style={{ flex: 1 }}>
                                        <SensorSlotCard sensor={repeater.sensors[idx]} compact={compact} gridMode={gridMode} sensorCount={sensorCount} T={T}
                                            onPress={() => onSensorPress(repeater.sensors[idx].raw)}
                                            onAnalysis={onSensorAnalysis ? () => onSensorAnalysis(repeater.sensors[idx].raw) : undefined} />
                                    </View>
                                );
                            })}
                        </View>
                    ))
                )}
            </View>
        </View>
    );
};

// ════════════════════════════════════════════════════════
// 구역 탭
// ════════════════════════════════════════════════════════
const AreaTab = ({ areas, current, onSelect, T, rightInset = 0, dense = false }: {
    areas: string[]; current: string; onSelect: (a: string) => void; T: Theme; rightInset?: number; dense?: boolean;
}) => (
    <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={[tabSt.bar, { backgroundColor: T.card, borderBottomColor: T.border }]}
        contentContainerStyle={[tabSt.content, dense ? { paddingVertical: 4 } : null, rightInset ? { paddingRight: 12 + rightInset } : null]}
    >
        {areas.map(area => {
            const active = area === current;
            return (
                <TouchableOpacity
                    key={area}
                    onPress={() => onSelect(area)}
                    style={[
                        tabSt.tab,
                        dense ? { paddingVertical: 3, paddingHorizontal: 10 } : null,
                        active
                            ? { backgroundColor: S.accent + '20', borderColor: S.accent + '55' }
                            : { backgroundColor: 'transparent', borderColor: 'transparent' },
                    ]}
                    activeOpacity={0.75}
                >
                    {active && <Dot color={S.accent} size={6} />}
                    <Text style={[tabSt.label, dense ? { fontSize: 13 } : null, {
                        color:      active ? S.accent : T.textSub,
                        fontWeight: active ? '700'    : '500',
                    }]}>{area}</Text>
                </TouchableOpacity>
            );
        })}
    </ScrollView>
);

// 범례 — 가로바와 펼침판이 같은 목록을 쓴다(둘로 복사하면 틀어진다)
const LEGEND_ITEMS = [
    { label: '기저귀 교체', color: S.diaper  },
    { label: '초기화 알림', color: S.ok      },
    { label: '움직임',      color: S.move    },
    { label: '움직임 없음', color: S.noMove  },
    { label: '센서 초기화', color: S.reset   },
    { label: '센서 교체',   color: S.replace },
    { label: '연결 끊김',   color: S.err     },
];

// ════════════════════════════════════════════════════════
// 그리드 토글 (4 / 8 / 16)
// ════════════════════════════════════════════════════════
const GridToggle = ({ mode, onChange, T }: {
    mode: GridMode; onChange: (m: GridMode) => void; T: Theme;
}) => (
    <View style={[gridSt.wrap, { backgroundColor: T.cardInner, borderColor: T.border }]}>
        {([4, 8, 16] as GridMode[]).map(opt => {
            const active = opt === mode;
            return (
                <TouchableOpacity
                    key={opt}
                    onPress={() => onChange(opt)}
                    style={[gridSt.btn, active
                        ? { backgroundColor: S.accent + '25', borderColor: S.accent + '55' }
                        : { borderColor: 'transparent' }
                    ]}
                    activeOpacity={0.75}
                >
                    <Text style={[gridSt.label, { color: active ? S.accent : T.muted }]}>{opt}분할</Text>
                </TouchableOpacity>
            );
        })}
    </View>
);

// ════════════════════════════════════════════════════════
// 메인
// ════════════════════════════════════════════════════════
function SensorScreenDetailInner({ navigation, route }: any) {
    const isFocused = useIsFocused();
    const { dark, setDark } = useDarkMode();
    const [gridMode, setGridMode]       = useState<GridMode>(4);
    // 폰에서는 4분할만 쓴다. 8·16분할은 중계기 카드가 더 작아져 센서 칸 내용이 들어가지 않는다.
    // 기준(900)은 App.tsx 의 헤더·드로어 분기와 같은 값이다.
    const { width: winW, height: winH } = useWindowDimensions();
    const isNarrow = winW < 900;
    // 실시간 알림 패널(290px 고정)은 넓은 화면에서만. 가로로 돌린 폰(약 850px)에서도
    // 290px 를 떼이면 방 카드가 너무 좁아져, 기준을 헤더·드로어와 같은 900 으로 맞춘다.
    const isWide   = !isNarrow;
    // 가로로 돌린 폰: 세로 공간이 400px 남짓이라 방을 2줄로 깔면 카드 안이 다 잘린다 → 1줄(2개)만.
    const isPhoneLandscape = isNarrow && winW > winH;
    useEffect(() => { if (isNarrow && gridMode !== 4) setGridMode(4); }, [isNarrow, gridMode]);

    // 다크모드 복원
    useEffect(() => {
        if (isFocused) {
            getStorage('darkMode').then((v: any) => { if (v !== null) setDark(v); });
            getStorage('AutoLogin').then((v: any) => { if (v) setAdmin(v.admin); });
        }
    }, [isFocused]);

    // 전체화면
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [legendOpen, setLegendOpen]   = useState(false);
    // 가로 폰은 높이가 400px 남짓이라 헤더+인포바(약 100px)가 1/4을 먹는다.
    // 전체화면과 같은 '몰입' 취급으로 걷어내고, 필요하면 버튼으로 잠깐 꺼내 본다.
    const [peek, setPeek] = useState(false);
    // 크롬(헤더·인포바)을 걷어낼 상황.
    // ★ isFullscreen·peek 선언 **뒤**에 와야 한다 — const 는 호이스팅이 안 돼서
    // 위에 두면 'Cannot access before initialization' 로 화면이 통째로 죽는다.
    const immersive = isFullscreen || (isPhoneLandscape && !peek);
    const toggleFullscreen = () => {
        try {
            const doc = document as any;
            if (!doc.fullscreenElement && !doc.webkitFullscreenElement) {
                const el = document.documentElement as any;
                (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
                setIsFullscreen(true);
            } else {
                (doc.exitFullscreen || doc.webkitExitFullscreen)?.call(doc);
                setIsFullscreen(false);
            }
        } catch {}
    };
    useEffect(() => {
        const handler = () => {
            const doc = document as any;
            setIsFullscreen(!!(doc.fullscreenElement || doc.webkitFullscreenElement));
        };
        document.addEventListener('fullscreenchange', handler);
        document.addEventListener('webkitfullscreenchange', handler);
        return () => { document.removeEventListener('fullscreenchange', handler); document.removeEventListener('webkitfullscreenchange', handler); };
    }, []);

    // 헤더 경계선 제거 + 전체화면이면 네비 헤더도 감춘다(방 카드만 남기기).
    // 브라우저 전체화면은 document 를 덮을 뿐 앱 헤더를 치워주지 않는다.
    useEffect(() => {
        navigation.setOptions({
            headerShadowVisible: false,
            headerShown: !immersive,
        });
    }, [navigation, immersive]);
    const [areas, setAreas]             = useState<string[]>([]);
    const [currentArea, setCurrentArea] = useState<string>(
        route?.params?.data?.AREA ?? route?.params?.area ?? ''
    );
    const [page, setPage] = useState(0);
    const [addModal, setAddModal] = useState<{ visible: boolean; repeaterId: string; label: string }>({ visible: false, repeaterId: '', label: '' });
    const [addRepeaterModal, setAddRepeaterModal] = useState(false);
    const [admin, setAdmin] = useState('1');
    const [warnings, setWarnings]         = useState<any[]>([]);
    const [warningVisible, setWarningVisible] = useState(false);
    const [reloadTick, setReloadTick] = useState(0);

    const [rawRepeaters, setRawRepeaters] = useState<any[]>([]);
    const [sensorMap, setSensorMap]       = useState<Record<string, any[]>>({});
    const [alarmList, setAlarmList]        = useState<AlarmItem[]>([]);
    const [initialLoaded, setInitialLoaded] = useState(false);
    // 소변알림 캐시: ALARM_TYPE=0 유지, -1/-3/-6 해제
    const urineCacheRef = useRef<Map<string, AlarmItem>>(new Map());

    const [newKeys, setNewKeys]   = useState<Set<string>>(new Set());
    const prevKeysRef             = useRef<Set<string>>(new Set());
    const newKeyTimers            = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    const prevAreaRef             = useRef(currentArea);
    const [disconnected, setDisconnected] = useState(false);
    const failCountRef            = useRef(0);

    const T = dark ? DARK : LIGHT;

    useEffect(() => {
        Data.fetchData('group/getAreaList', {}, (result: any, count: any) => {
            if (count > 0) {
                const sorted = [...result].sort((a: any, b: any) => {
                    if (a.AREA === '로밍') return 1;
                    if (b.AREA === '로밍') return -1;
                    return a.AREA.localeCompare(b.AREA, 'ko');
                });
                setAreas(sorted.map((a: any) => a.AREA));
            }
        }, true);
    }, []);

    useEffect(() => {
        if (!currentArea && areas.length > 0) setCurrentArea(areas[0]);
    }, [areas, currentArea]);

    const loadGenRef = useRef(0);
    useEffect(() => {
        if (!currentArea) return;
        let interval: ReturnType<typeof setInterval> | null = null;
        let cancelled = false;
        const loadData = () => {
            const gen = ++loadGenRef.current;
            const onFetchError = () => {
                failCountRef.current++;
                if (failCountRef.current >= 2) setDisconnected(true);
            };
            const onFetchOk = () => {
                failCountRef.current = 0;
                setDisconnected(false);
            };
            // 1) 중계기 목록 → 각 중계기별 센서 조회
            Data.fetchData('repeater/getRepeatersList', { AREA: currentArea },
                (rpResult: any, rpCount: any) => {
                    if (cancelled || gen !== loadGenRef.current) return;
                    onFetchOk();
                    const rps = rpCount > 0 ? rpResult : [];
                    if (rps.length === 0) {
                        setRawRepeaters([]);
                        setSensorMap({});
                        setInitialLoaded(true);
                        return;
                    }
                    // 리피터별 센서 조회 → 맵으로 저장
                    const newMap: Record<string, any[]> = {};
                    let completed = 0;
                    rps.forEach((rp: any) => {
                        Data.fetchData('repeater/getSensorListByRepeater',
                            { REPEATER_ID: rp.ID },
                            (sResult: any, sCount: any) => {
                                if (cancelled || gen !== loadGenRef.current) return;
                                newMap[rp.ID] = sCount > 0 ? sResult : [];
                                completed++;
                                if (completed === rps.length) {
                                    setRawRepeaters(rps);
                                    setSensorMap({ ...newMap });
                                    setInitialLoaded(true);
                                }
                            }, onFetchError);
                    });
                }, onFetchError);
            // 2) 알림 리스트 (별도) + 소변알림 캐시
            Data.fetchData('group/getAlarmList', {},
                (result: any, count: any) => {
                    if (cancelled || gen !== loadGenRef.current) return;
                    onFetchOk();
                    const raw = count > 0 ? (result as AlarmItem[]) : [];
                    const areaRaw = raw.filter(a => a.AREA === currentArea);
                    const cache = urineCacheRef.current;

                    // 알림캐시: 0/-3→시간고정+유지, -1→캐시해제
                    areaRaw.forEach(a => {
                        if (a.ALARM_TYPE === 0 || a.ALARM_TYPE === -3) {
                            // AMOUNT=-2(중계기변경) 행도 캐시에 넣는다. 예전엔 통째로 걸러서, 중계기 두 대에
                            // 번갈아 잡히는 센서는 젖어도 알림이 안 떴다 — ALARM_TYPE=0 은 캐시를 통해서만
                            // 화면에 올라가고, LAST_VALUE 는 -2 행에서도 정상으로 이어지는데 행째로 버렸다.
                            // 단 -2 행의 VALUE 는 예측량이 아니라 원시 커패시턴스(2500대, KIND=0)라 쓰지 않는다.
                            const cached = cache.get(a.SENSOR_ID);
                            if (!cached || cached.ALARM_TYPE !== a.ALARM_TYPE) {
                                cache.set(a.SENSOR_ID, {
                                    ...a,
                                    VALUE: a.AMOUNT === -2 ? (cached ? cached.VALUE : null) : a.VALUE,
                                });
                            } else {
                                cached.LAST_VALUE = a.LAST_VALUE;
                                if (a.AMOUNT !== -2) cached.VALUE = a.VALUE;
                            }
                        } else if (a.ALARM_TYPE === -1) {
                            cache.delete(a.SENSOR_ID);
                        }
                    });

                    const otherAlarms = areaRaw.filter(a =>
                        a.ALARM_TYPE !== -1 && a.ALARM_TYPE !== 0 && a.ALARM_TYPE !== -3);
                    const cachedAlarms = [...cache.values()];
                    setAlarmList([...otherAlarms, ...cachedAlarms]);
                }, onFetchError);
        };
        if (isFocused) { loadData(); interval = setInterval(loadData, 10000); }
        return () => { cancelled = true; if (interval) clearInterval(interval); };
    }, [isFocused, currentArea, reloadTick]);

    const repeaters = useMemo(() => {
        if (rawRepeaters.length === 0) return [];
        return buildRepeaters(rawRepeaters, sensorMap, alarmList);
    }, [rawRepeaters, sensorMap, alarmList]);

    const offlineSensorIds = useMemo(() => {
        const ids = new Set<string>();
        Object.values(sensorMap).flat().forEach((s: any) => {
            if (s.CONNECT !== 0) ids.add(s.SENSOR_ID);
        });
        return ids;
    }, [sensorMap]);

    const filteredAlarms = useMemo(() => {
        const base = alarmList.filter(a => !offlineSensorIds.has(a.SENSOR_ID));
        // standup 센서 → 움직임 알림으로 피드에 추가
        const moveAlarms: AlarmItem[] = [];
        repeaters.forEach(rp => {
            rp.sensors.forEach(s => {
                if (s.posture && s.posture.toLowerCase().includes('standup')) {
                    moveAlarms.push({
                        AREA: currentArea,
                        SENSOR_ID: s.sensor_id,
                        NAME: s.name || '',
                        ROOM_NO: rp.label,
                        AMOUNT: 0,
                        LAST_VALUE: 0,
                        VALUE: 0,
                        ALARM_TYPE: -7,
                        REGDATE: 'move',
                    });
                }
            });
        });
        return [...moveAlarms, ...base];
    }, [alarmList, offlineSensorIds, repeaters, currentArea]);

    // filteredAlarms 변경 시 NEW 깜빡임 추적
    useEffect(() => {
        const currentKeys = new Set(filteredAlarms.map(alarmKey));
        const added = [...currentKeys].filter(k => !prevKeysRef.current.has(k));
        if (added.length > 0) {
            setNewKeys(prev => {
                const next = new Set(prev);
                added.forEach(k => next.add(k));
                return next;
            });
            added.forEach(k => {
                if (newKeyTimers.current.has(k)) clearTimeout(newKeyTimers.current.get(k)!);
                const t = setTimeout(() => {
                    setNewKeys(prev => { const n = new Set(prev); n.delete(k); return n; });
                    newKeyTimers.current.delete(k);
                }, BLINK_DURATION_MS);
                newKeyTimers.current.set(k, t);
            });
        }
        prevKeysRef.current = currentKeys;
    }, [filteredAlarms]);

    const totalSensors = repeaters.reduce((acc, rp) => acc + rp.sensors.length, 0);

    // 구역 변경 시 페이지 리셋 + 소변알림 캐시 초기화
    useEffect(() => {
        if (currentArea !== prevAreaRef.current) {
            prevAreaRef.current = currentArea;
            setPage(0);
            urineCacheRef.current.clear();
            setAlarmList([]);
        }
    }, [currentArea]);

    // 페이징
    const cols      = gridMode === 4 ? 2 : 4;
    // 가로 폰은 1줄만 — 2개씩 보고 페이지로 넘긴다.
    const totalRows = isPhoneLandscape ? 1 : gridMode / cols; // 4→2행, 8→2행, 16→4행
    const perPage   = cols * totalRows;
    const pageCount = Math.max(1, Math.ceil(repeaters.length / perPage));
    const paged     = repeaters.slice(page * perPage, (page + 1) * perPage);

    useEffect(() => { setPage(0); }, [gridMode, isPhoneLandscape]);
    useEffect(() => { if (!isPhoneLandscape) setPeek(false); }, [isPhoneLandscape]);

    // WARNING 경고 폴링
    useEffect(() => {
        if (!isFocused) return;
        const checkWarnings = () => {
            Data.fetchData('sensor/getWarnings', {}, (result: any, count: any) => {
                if (count > 0) {
                    setWarnings(result);
                    setWarningVisible(true);
                }
            }, true);
        };
        checkWarnings();
        const wi = setInterval(checkWarnings, 10000);
        return () => clearInterval(wi);
    }, [isFocused]);

    const dismissWarning = (idx: number) => {
        Data.fetchData('sensor/dismissWarning', { IDX: idx }, () => {
            setWarnings(prev => prev.filter(w => w.IDX !== idx));
            if (warnings.length <= 1) setWarningVisible(false);
        }, true);
    };

    const dismissAllWarnings = () => {
        Data.fetchData('sensor/dismissWarning', {}, () => {}, true);
        setWarnings([]);
        setWarningVisible(false);
    };

    return (
        <ThemeContext.Provider value={{ dark, toggle: () => setDark(!dark) }}>
            <View style={[mainSt.container, { backgroundColor: T.bg }]}>

                {/* WARNING 경고 모달 */}
                <Modal visible={warningVisible} transparent animationType="fade">
                    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.7)' }}>
                        <View style={{ backgroundColor: '#1E293B', borderRadius: 16, padding: 24, width: '90%', maxWidth: 500, borderWidth: 2, borderColor: '#F87171' }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
                                <Ionicons name="warning" size={28} color="#F87171" />
                                <Text style={{ color: '#F87171', fontSize: 20, fontWeight: 'bold', marginLeft: 8 }}>경고 구역 감지</Text>
                            </View>
                            {warnings.map((w: any, i: number) => (
                                <View key={w.IDX || i} style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.1)' }}>
                                    <View>
                                        <Text style={{ color: '#E2E8F0', fontSize: 15, fontWeight: '600' }}>{maskName(w.NAME) || w.SENSOR_ID}</Text>
                                        <Text style={{ color: '#94A3B8', fontSize: 12, marginTop: 2 }}>{w.AREA} {w.ROOM_NO} | RSSI: {w.RSSI}</Text>
                                        <Text style={{ color: '#64748B', fontSize: 11, marginTop: 2 }}>{w.REGDATE}</Text>
                                    </View>
                                    <TouchableOpacity onPress={() => dismissWarning(w.IDX)} style={{ backgroundColor: '#334155', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6 }}>
                                        <Text style={{ color: '#94A3B8', fontSize: 12 }}>확인</Text>
                                    </TouchableOpacity>
                                </View>
                            ))}
                            <TouchableOpacity onPress={dismissAllWarnings} style={{ backgroundColor: '#F87171', borderRadius: 10, paddingVertical: 12, marginTop: 16, alignItems: 'center' }}>
                                <Text style={{ color: '#FFF', fontSize: 15, fontWeight: 'bold' }}>모두 확인</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </Modal>

                {/* 인포바 — 전체화면·가로폰에서는 숨긴다(방 카드만 보이게) */}
                {!immersive && (
                <View style={[mainSt.infoBar, { backgroundColor: T.card, borderBottomColor: T.border }]}>
                    <Text style={[mainSt.infoTitle, { color: T.textSub }]}>
                        중계기 {repeaters.length}대 · 센서 {totalSensors}개
                    </Text>
                    {/* 범례 — 넓으면 가로로 펼치고, 좁으면 칩 하나로 접는다.
                        7개를 좁은 화면에 그대로 두면 세로로 쌓이며 좌우 요소와 뒤엉킨다. */}
                    {isNarrow ? (
                        <TouchableOpacity
                            onPress={() => setLegendOpen(v => !v)}
                            style={[mainSt.legendChip, { borderColor: T.border, backgroundColor: T.cardInner }]}
                            activeOpacity={0.75}
                        >
                            <Ionicons name="information-circle-outline" size={13} color={T.textSub} />
                            <Text style={[mainSt.legendLabel, { color: T.textSub }]}>범례</Text>
                            <Ionicons name={legendOpen ? 'chevron-up' : 'chevron-down'} size={12} color={T.muted} />
                        </TouchableOpacity>
                    ) : (
                        <View style={mainSt.legendBar}>
                            {LEGEND_ITEMS.map(it => (
                                <View key={it.label} style={mainSt.legendItem}>
                                    <Dot color={it.color} size={6} />
                                    <Text style={[mainSt.legendLabel, { color: T.textSub }]}>{it.label}</Text>
                                </View>
                            ))}
                        </View>
                    )}
                    <View style={mainSt.infoRight}>
                        {admin === '0' && (
                        <TouchableOpacity
                            onPress={() => setAddRepeaterModal(true)}
                            style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: S.accent, borderRadius: 8, paddingHorizontal: 10, height: 30, gap: 4 }}
                        >
                            <Ionicons name="add" size={16} color="#fff" />
                            <Text style={{ fontSize: 12, fontWeight: '700', color: '#fff' }}>중계기</Text>
                        </TouchableOpacity>
                        )}
                        {!isNarrow && <GridToggle mode={gridMode} onChange={setGridMode} T={T} />}
                        <TouchableOpacity
                            onPress={toggleFullscreen}
                            style={[mainSt.iconBtn, { backgroundColor: T.cardInner, borderColor: T.border }]}
                        >
                            <Ionicons name={isFullscreen ? 'contract-outline' : 'expand-outline'} size={16} color={S.accent} />
                        </TouchableOpacity>
                    </View>
                </View>
                )}

                {/* 접어둔 범례 펼침판 */}
                {!immersive && isNarrow && legendOpen && (
                    <View style={[mainSt.legendPanel, { backgroundColor: T.card, borderBottomColor: T.border }]}>
                        {LEGEND_ITEMS.map(it => (
                            <View key={it.label} style={mainSt.legendItem}>
                                <Dot color={it.color} size={6} />
                                <Text style={[mainSt.legendLabel, { color: T.textSub }]}>{it.label}</Text>
                            </View>
                        ))}
                    </View>
                )}

                {/* 연결 끊김 표시 */}
                {disconnected && (
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: S.err + '18', paddingVertical: 6, gap: 6 }}>
                        <Ionicons name="cloud-offline-outline" size={14} color={S.err} />
                        <Text style={{ fontSize: 12, fontWeight: '600', color: S.err }}>
                            서버 연결 끊김 — 자동 재연결 중...
                        </Text>
                    </View>
                )}

                {/* 구역 탭 */}
                <AreaTab areas={areas} current={currentArea} onSelect={a => setCurrentArea(a)} T={T}
                    rightInset={(isFullscreen || isPhoneLandscape) ? 44 : 0}
                    dense={isPhoneLandscape} />

                {/* 떠 있는 버튼 — 인포바를 숨기므로 이것만 남는다(폰엔 ESC 가 없다).
                    전체화면이면 '나가기', 가로 폰이면 '메뉴 꺼내기/감추기'. */}
                {(isFullscreen || isPhoneLandscape) && (
                    <TouchableOpacity
                        onPress={() => { if (isFullscreen) toggleFullscreen(); else setPeek(v => !v); }}
                        style={[mainSt.fsExit, { backgroundColor: T.card, borderColor: T.border }]}
                        activeOpacity={0.8}
                    >
                        <Ionicons
                            name={isFullscreen ? 'contract-outline' : (peek ? 'chevron-up' : 'ellipsis-horizontal')}
                            size={16} color={S.accent} />
                    </TouchableOpacity>
                )}

                {/* 본문 */}
                <View style={mainSt.body}>
                    <View style={mainSt.leftPane}>
                        {/* 그리드 - absolute로 부모 크기 즉시 채움 */}
                        <View style={mainSt.gridWrap}>
                            <View style={{ flex: 1 }}>
                                {repeaters.length === 0 && initialLoaded ? (
                                    <View style={mainSt.emptyBox}>
                                        <Ionicons name="radio-outline" size={40} color={T.muted} />
                                        <Text style={[mainSt.emptyText, { color: T.muted }]}>등록된 중계기가 없습니다.</Text>
                                    </View>
                                ) : (
                                    Array.from({ length: totalRows }, (_, row) => (
                                        <View key={row} style={mainSt.gridRow}>
                                            {Array.from({ length: cols }, (_, col) => {
                                                const rp = paged[row * cols + col];
                                                if (!rp) return <View key={col} style={{ flex: 1, margin: 5 }} />;
                                                return (
                                                    <View key={rp.id} style={{ flex: 1, margin: 5 }}>
                                                        <RepeaterCard repeater={rp} gridMode={gridMode} T={T}
                                                            isViewer={admin === '2'}
                                                            onAdd={() => setAddModal({ visible: true, repeaterId: rp.id, label: rp.label })}
                                                            onSensorPress={(raw) => { if (admin === '2') return; try { window.localStorage.setItem(`_navParams_SensorDetail_${raw?.SENSOR_ID}`, JSON.stringify({ data: raw })); } catch {} navigation.navigate('SensorDetail', { id: String(raw?.SENSOR_ID) }); }}
                                                            onSensorAnalysis={(raw) => navigation.navigate('SensorAnalysis', { sensorID: raw?.SENSOR_ID })} />
                                                    </View>
                                                );
                                            })}
                                        </View>
                                    ))
                                )}
                            </View>
                            {/* 페이지네이션 */}
                            {pageCount > 1 && (
                                <View style={[mainSt.pagination, isPhoneLandscape ? { paddingVertical: 3, gap: 8 } : null, { backgroundColor: T.card, borderTopColor: T.border }]}>
                                    <TouchableOpacity
                                        onPress={() => setPage(p => Math.max(0, p - 1))}
                                        disabled={page === 0}
                                        style={[mainSt.pageBtn, isPhoneLandscape ? { width: 24, height: 24 } : null, { backgroundColor: T.cardInner, borderColor: T.border, opacity: page === 0 ? 0.3 : 1 }]}
                                    >
                                        <Ionicons name="chevron-back" size={15} color={T.text} />
                                    </TouchableOpacity>
                                    <View style={mainSt.pageDots}>
                                        {Array.from({ length: pageCount }, (_, i) => (
                                            <TouchableOpacity key={i} onPress={() => setPage(i)}>
                                                <View style={[
                                                    mainSt.pageDot,
                                                    { backgroundColor: i === page ? S.accent : T.border, width: i === page ? 18 : 7 }
                                                ]} />
                                            </TouchableOpacity>
                                        ))}
                                    </View>
                                    <Text style={[mainSt.pageLabel, { color: T.muted }]}>{page + 1} / {pageCount}</Text>
                                    <TouchableOpacity
                                        onPress={() => setPage(p => Math.min(pageCount - 1, p + 1))}
                                        disabled={page === pageCount - 1}
                                        style={[mainSt.pageBtn, isPhoneLandscape ? { width: 24, height: 24 } : null, { backgroundColor: T.cardInner, borderColor: T.border, opacity: page === pageCount - 1 ? 0.3 : 1 }]}
                                    >
                                        <Ionicons name="chevron-forward" size={15} color={T.text} />
                                    </TouchableOpacity>
                                </View>
                            )}
                        </View>
                    </View>

                    {isWide && (
                        <View style={[mainSt.rightPane, { borderLeftColor: T.border }]}>
                            <AlarmFeed alarms={filteredAlarms} newKeys={newKeys} />
                        </View>
                    )}
                </View>

                {/* 센서 등록 모달 */}
                <AddSensorModal
                    visible={addModal.visible}
                    onClose={(success) => {
                        setAddModal({ visible: false, repeaterId: '', label: '' });
                        if (success) setTimeout(() => setReloadTick(t => t + 1), 500);
                    }}
                    repeaterId={addModal.repeaterId}
                    area={currentArea}
                    roomNo={addModal.label}
                    T={T}
                />
                <AddRepeaterModal
                    visible={addRepeaterModal}
                    onClose={(success) => {
                        setAddRepeaterModal(false);
                        if (success) setTimeout(() => setReloadTick(t => t + 1), 500);
                    }}
                    area={currentArea}
                    T={T}
                />
            </View>
        </ThemeContext.Provider>
    );
}

// ════════════════════════════════════════════════════════
// StyleSheet
// ════════════════════════════════════════════════════════

const mainSt = StyleSheet.create({
    container:   { flex: 1, flexBasis: 0 },
    // 좁은 화면: 범례(legendBar, flex:1)가 세로로 길게 쌓이면서 좌우 요소와 겹쳐 보였다.
    // infoBar 자체가 접히게 하고 범례에 minWidth 를 줘서 제 줄로 내려가게 한다.
    infoBar:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 8, paddingHorizontal: 16, paddingVertical: 8, borderBottomWidth: 1 },
    infoTitle:   { fontSize: 14, fontWeight: '600' },
    legendBar:   { flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, minWidth: 260, justifyContent: 'center', flexWrap: 'wrap' },
    legendItem:  { flexDirection: 'row', alignItems: 'center', gap: 4 },
    legendChip:  { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 9, paddingVertical: 5, borderRadius: 999, borderWidth: 1 },
    legendPanel: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, rowGap: 6, paddingHorizontal: 16, paddingVertical: 8, borderBottomWidth: 1 },
    fsExit:      { position: 'absolute' as any, top: 10, right: 10, zIndex: 50, width: 34, height: 34, borderRadius: 17, borderWidth: 1, justifyContent: 'center', alignItems: 'center', opacity: 0.9 },
    legendLabel: { fontSize: 12, fontWeight: '500' },
    infoRight:   { flexDirection: 'row', alignItems: 'center', gap: 8 },
    iconBtn:     { width: 30, height: 30, borderRadius: 8, justifyContent: 'center', alignItems: 'center', borderWidth: 1 },
    body:        { flex: 1, flexBasis: 0, flexDirection: 'row', overflow: 'hidden' },
    leftPane:    { flex: 1, flexBasis: 0, overflow: 'hidden', position: 'relative' as any },
    rightPane:   { width: 290, borderLeftWidth: 1 },
    // 그리드: absolute로 부모 크기 즉시 채움 (flex 재계산 지연 방지)
    gridWrap:    { position: 'absolute' as any, top: 0, left: 0, right: 0, bottom: 0, padding: 5, display: 'flex' as any, flexDirection: 'column' as any },
    gridRow:     { flex: 1, flexDirection: 'row' },
    pagination:  { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingHorizontal: 16, paddingVertical: 8, gap: 10, borderTopWidth: 1 },
    pageBtn:     { width: 28, height: 28, borderRadius: 7, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
    pageDots:    { flexDirection: 'row', alignItems: 'center', gap: 5, flex: 1, justifyContent: 'center' },
    pageDot:     { height: 7, borderRadius: 4 },
    pageLabel:   { fontSize: 13, fontWeight: '600', minWidth: 36, textAlign: 'right' },
    emptyBox:    { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12 },
    emptyText:   { fontSize: 16 },
});

const tabSt = StyleSheet.create({
    bar:     { borderBottomWidth: 1, flexGrow: 0 },
    content: { paddingHorizontal: 12, paddingVertical: 7, gap: 4, flexDirection: 'row' },
    tab:     { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8, borderWidth: 1 },
    label:   { fontSize: 15 },
});

const gridSt = StyleSheet.create({
    wrap:  { flexDirection: 'row', borderRadius: 8, borderWidth: 1, padding: 2, gap: 2 },
    btn:   { paddingHorizontal: 9, paddingVertical: 4, borderRadius: 6, borderWidth: 1 },
    label: { fontSize: 13, fontWeight: '700' },
});

const rpSt = StyleSheet.create({
    card:       { borderRadius: 12, borderWidth: 1, borderTopWidth: 3, overflow: 'hidden' },
    header:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 8, paddingVertical: 7 },
    headerLeft: { flexDirection: 'row', alignItems: 'center', gap: 6, flex: 1 },
    iconBox:    { width: 24, height: 24, borderRadius: 6, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
    rpLabel:    { fontWeight: '700' },
    addBtn:     { width: 18, height: 18, borderRadius: 5, borderWidth: 1, alignItems: 'center', justifyContent: 'center', marginLeft: 2 },
    chip:       { flexDirection: 'row', alignItems: 'center', gap: 3, borderRadius: 8, paddingHorizontal: 6, paddingVertical: 2 },
    chipText:   { fontSize: 11, fontWeight: '700' },
    sensorGrid: { paddingHorizontal: 6, paddingBottom: 6, gap: 4 },
});

const slotSt = StyleSheet.create({
    // 풀 카드
    card:        { flex: 1, minWidth: 0, overflow: 'hidden', borderRadius: 8, borderWidth: 1, borderLeftWidth: 2.5, padding: 7 },
    cardTop:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 },
    iconRow:     { flexDirection: 'row', alignItems: 'center', gap: 5 },
    batBadge:    { flexDirection: 'row', alignItems: 'center', gap: 2, borderRadius: 4, borderWidth: 1, paddingHorizontal: 3, paddingVertical: 1 },
    batText:     { fontSize: 11, fontWeight: '700' },
    sensorName:  { fontSize: 14, fontWeight: '700' },
    statusRow:   { flexDirection: 'row', alignItems: 'center', gap: 4, flexWrap: 'wrap' },
    statusLabel: { fontSize: 12, fontWeight: '600', flex: 1 },
    valueBadge:  { borderWidth: 1, borderRadius: 4, paddingHorizontal: 4, paddingVertical: 1 },
    valueText:   { fontSize: 12, fontWeight: '700' },
    lastSeen:    { fontSize: 11 },
    // 컴팩트 카드
    compactCard:   { flex: 1, borderRadius: 6, borderWidth: 1, borderLeftWidth: 2, padding: 4 },
    compactIcons:  { flexDirection: 'row', alignItems: 'center', gap: 3, justifyContent: 'flex-end' },
    compactName:   { fontSize: 11, fontWeight: '700' },
    compactStatus: { fontSize: 9, fontWeight: '600' },
});

const alarmSt = StyleSheet.create({
    item:          { flexDirection: 'row', alignItems: 'center', gap: 8, borderRadius: 10, padding: 10, borderLeftWidth: 3, overflow: 'hidden' },
    newBadge:      { position: 'absolute', top: 0, right: 0, paddingHorizontal: 6, paddingVertical: 2, borderBottomLeftRadius: 8 },
    newBadgeText:  { fontSize: 10, fontWeight: '800', color: '#fff', letterSpacing: 0.8 },
    dotWrap:       { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
    repeaterLabel: { fontSize: 13, fontWeight: '700' },
    sub:           { fontSize: 12 },
    msg:           { fontSize: 12, fontWeight: '600' },
    time:          { fontSize: 11 },
    badge:         { borderWidth: 1, borderRadius: 6, paddingHorizontal: 5, paddingVertical: 2 },
    badgeText:     { fontSize: 12, fontWeight: '700' },
});

const apSt = StyleSheet.create({
    panel:       { flex: 1 },
    header:      { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14, borderBottomWidth: 1 },
    headerLeft:  { flexDirection: 'row', alignItems: 'center', gap: 7 },
    title:       { fontSize: 13, fontWeight: '700', letterSpacing: 1.1, textTransform: 'uppercase' },
    liveBadge:   { flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: 'rgba(248,113,113,0.12)', borderRadius: 8, paddingHorizontal: 7, paddingVertical: 3 },
    liveText:    { fontSize: 12, color: S.err, fontWeight: '700' },
    feedList:    { padding: 12, gap: 8 },
    empty:       { alignItems: 'center', paddingVertical: 32, gap: 10 },
    emptyDot:    { width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center' },
    emptyText:   { fontSize: 14 },
    scrollTrack: { position: 'absolute', right: 0, top: 0, bottom: 0, width: 3, borderRadius: 2 },
    scrollThumb: { position: 'absolute', left: 0, right: 0, borderRadius: 2 },
});

const modalSt = StyleSheet.create({
    overlay:      { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center', alignItems: 'center' },
    container:    { width: 460, maxHeight: '88%', borderRadius: 16, borderWidth: 1, overflow: 'hidden' },
    header:       { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20, paddingVertical: 16, borderBottomWidth: 1 },
    title:        { fontSize: 18, fontWeight: '700' },
    closeBtn:     { width: 32, height: 32, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
    body:         { paddingHorizontal: 20, paddingTop: 16 },
    section:      { borderWidth: 1, borderRadius: 12, padding: 14, gap: 10 },
    sectionTitle: { fontSize: 14, fontWeight: '700' },
    label:        { fontSize: 12, fontWeight: '600', marginBottom: 2 },
    input:        { borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8, fontSize: 14 },
    dropdown:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10 },
    dropdownList: { borderWidth: 1, borderRadius: 8, marginTop: 4, overflow: 'hidden' },
    dropdownItem: { paddingHorizontal: 12, paddingVertical: 10, borderBottomWidth: 1 },
    searchInput:  { margin: 6, borderWidth: 1, borderRadius: 6, paddingHorizontal: 10, paddingVertical: 6, fontSize: 13 },
    radioBtn:     { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 7, borderRadius: 8, borderWidth: 1 },
    radioDot:     { width: 14, height: 14, borderRadius: 7, borderWidth: 2 },
    footer:       { flexDirection: 'row', gap: 10, padding: 16, borderTopWidth: 1, justifyContent: 'flex-end' },
    btn:          { paddingHorizontal: 24, paddingVertical: 10, borderRadius: 8, borderWidth: 1 },
});

export default function SensorScreenDetail(props: any) {
    return (
        <ErrorBoundary>
            <SensorScreenDetailInner {...props} />
        </ErrorBoundary>
    );
}
